import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  ChartPalette,
  ChartPaletteConfig,
  ChartPaletteId,
  ChartPaletteIds,
  ChartType,
  createDefaultPaletteConfig,
  getPaletteById,
  getPaletteIdForChartType,
  normalizePaletteConfig,
} from "@ark/shared";

interface ChartThemeContextValue {
  readonly getPaletteForChartType: (chartType?: ChartType) => ChartPalette;
  readonly config: ChartPaletteConfig;
  readonly setConfig: (config: ChartPaletteConfig) => void;
  // Legacy support
  readonly currentPalette: ChartPalette;
  readonly setPaletteId: (id: ChartPaletteId) => void;
}

export const ChartThemeContext = createContext<ChartThemeContextValue | null>(null);

interface Props {
  readonly initialPaletteId?: string;
  readonly initialConfig?: ChartPaletteConfig;
  readonly children: React.ReactNode;
  readonly onPaletteChange?: (id: ChartPaletteId) => void;
  readonly onConfigChange?: (config: ChartPaletteConfig) => void;
}

export const ChartThemeProvider = ({
  initialPaletteId,
  initialConfig,
  children,
  onPaletteChange,
  onConfigChange,
}: Props) => {
  // Normalize initial config from either config object or legacy palette ID
  const normalizedInitialConfig = useMemo(() => {
    if (initialConfig) {
      return normalizePaletteConfig(initialConfig);
    }
    if (initialPaletteId) {
      return normalizePaletteConfig(initialPaletteId);
    }
    return createDefaultPaletteConfig(ChartPaletteIds.Default);
  }, [initialConfig, initialPaletteId]);

  // Sync with external config changes
  useEffect(() => {
    if (initialConfig) {
      const normalized = normalizePaletteConfig(initialConfig);
      // Use functional update to ensure we always update if config changed
      setLocalConfig((prev) => {
        // Deep comparison to detect changes
        const prevStr = JSON.stringify(prev);
        const normalizedStr = JSON.stringify(normalized);
        if (prevStr !== normalizedStr) {
          return normalized;
        }
        return prev;
      });
    } else if (initialPaletteId) {
      const normalized = normalizePaletteConfig(initialPaletteId);
      setLocalConfig(normalized);
    }
  }, [initialConfig, initialPaletteId]);

  const [localConfig, setLocalConfig] = useState<ChartPaletteConfig>(normalizedInitialConfig);

  const handleSetConfig = useCallback(
    (config: ChartPaletteConfig) => {
      setLocalConfig(config);
      if (onConfigChange) {
        onConfigChange(config);
      }
      // Also trigger legacy callback for backward compatibility
      if (onPaletteChange) {
        onPaletteChange(config.default);
      }
    },
    [onConfigChange, onPaletteChange],
  );

  const handleSetPalette = useCallback(
    (id: ChartPaletteId) => {
      const newConfig = createDefaultPaletteConfig(id);
      handleSetConfig(newConfig);
    },
    [handleSetConfig],
  );

  const getPaletteForChartType = useCallback(
    (chartType: ChartType = "default"): ChartPalette => {
      const paletteId = getPaletteIdForChartType(localConfig, chartType);
      return getPaletteById(paletteId, localConfig);
    },
    [localConfig],
  );

  const value = useMemo(
    () => ({
      getPaletteForChartType,
      config: localConfig,
      setConfig: handleSetConfig,
      // Legacy support
      currentPalette: getPaletteForChartType("default"),
      setPaletteId: handleSetPalette,
    }),
    [getPaletteForChartType, localConfig, handleSetConfig, handleSetPalette],
  );

  return <ChartThemeContext.Provider value={value}>{children}</ChartThemeContext.Provider>;
};

export const useChartTheme = () => {
  const ctx = useContext(ChartThemeContext);
  if (!ctx) {
    throw new Error("useChartTheme must be used within ChartThemeProvider");
  }
  return ctx;
};
